/**
 * Ticket Lifecycle v2 — Lifecycle OptiTime display-state helper
 * (Slice 5C UI + slice 5C.1 lower-bound polish).
 *
 * Pure decision helper that turns a `calculateLifecycleOptiTimeEstimate`
 * result into a presentational shape for the "Lifecycle OptiTime" tile
 * on `TicketLifecycleTimeCard`. Keeping the copy + render-decision
 * rules out of the component makes them unit-testable without DOM and
 * lets the component stay presentational.
 *
 * Scope: **read-only display state only.** Does NOT mutate anything.
 * Does NOT feed Tech/Bay OptiTime, Chronomancer, scheduling, smart
 * assign, timer, parking, rollover, bay, invariants, or any mutation
 * path. The component mounting this helper is flag-gated by
 * `custodyHandoff` (no new flag).
 *
 * Decision tree (first matching rule wins):
 *   1. `method === 'insufficient_data'` → render `'—'` and the
 *      "Tech OptiTime unavailable" note. The tile still renders so the
 *      user knows the surface exists; only the value is suppressed.
 *   2. `method === 'tech_only'`         → set `lowerBound: true` and
 *      render the (clamped) tech value with a "lower bound" note. The
 *      card renders the value with a leading `'≥ '` so a partial
 *      estimate cannot be mistaken for a final one. The note text is
 *      tailored from `advisorDelayReason` when supplied — e.g.
 *      `not_approved` → "advisor delay is calculated after the ticket
 *      reaches approved/work status" — so users see *why* it's a
 *      lower bound instead of a generic unavailable message.
 *   3. `method === 'tech_plus_advisor_delay'` → render the lifecycle
 *      sum with the "Tech time + advisor delay" note. This is true
 *      even when advisor delay is exactly 0 (a *reliable* 0 from the
 *      helper, e.g. `work_only`) — the lifecycle estimate is still
 *      properly anchored on both inputs.
 *
 * Sublabel:
 *   `'Estimated vehicle time in shop'` — surfaced separately so callers
 *   can render it as a small dim caption beside the label. Constant
 *   across all methods; the per-method copy belongs in `note`.
 *
 * Boundary: this module is analytics-only / display-state only. It must
 * not be imported into Chronomancer / scheduling / timer / parking /
 * rollover / bay / invariants hot paths. The drift detector boundary
 * guard pins this.
 */

import type { AdvisorDelayImpactDisplayReason } from './advisorDelayImpactDisplay';
import {
  calculateLifecycleOptiTimeEstimate,
  type LifecycleOptiTimeEstimateInput,
  type LifecycleOptiTimeEstimateResult,
  type LifecycleOptiTimeMethod,
} from './lifecycleOptiTime';

export type LifecycleOptiTimeDisplayReason =
  | 'value'
  | 'predicted_advisor_delay'
  | 'lower_bound_tech_only'
  | 'insufficient_data';

/**
 * Optional Slice 5C.1 input: the Advisor Delay tile's `reason` value.
 * Reusing that enum keeps the two tiles in lockstep — if the Advisor
 * Delay tile refuses for `not_approved`, the Lifecycle OptiTime tile
 * can say the *same* thing instead of a generic "advisor delay
 * unavailable". Passed through `null`/`undefined` when the Advisor
 * Delay tile committed to a value (or wasn't rendered) — the helper
 * then falls back to a generic lower-bound note.
 */
export interface LifecycleOptiTimeDisplayInput
  extends LifecycleOptiTimeEstimateInput {
  advisorDelayReason?: AdvisorDelayImpactDisplayReason | null;
}

export interface LifecycleOptiTimeDisplayState {
  /** When `false`, callers should hide the tile entirely. Slice 5C
   *  always renders the tile when the card is visible, but the field is
   *  declared so future surfaces can choose to suppress. */
  shouldRender: boolean;
  /** Tile label — constant. Owned here so the drift detector can pin it
   *  and a future copy review touches a single file. */
  label: 'Lifecycle OptiTime';
  /** Short subtitle that explains what the number means without
   *  scheduling implications. */
  sublabel: 'Estimated vehicle time in shop';
  /** Minutes to render; `null` ⇒ render `'—'`. Always non-negative when
   *  set (mirrors `calculateLifecycleOptiTimeEstimate` clamping). */
  valueMinutes: number | null;
  /**
   * Slice 5C.1: when `true`, the card prefixes the formatted minutes
   * with `'≥ '` so the number reads as a minimum/lower-bound estimate
   * rather than a final one. Always paired with `method === 'tech_only'`
   * today; declared as its own boolean so the rendering decision lives
   * in one place rather than duplicating method comparisons in the
   * component.
   */
  lowerBound: boolean;
  /** Human-readable note below the value. */
  note: string;
  /** Method echoed from the underlying helper for telemetry/tests. */
  method: LifecycleOptiTimeMethod;
  /** Machine-friendly reason for tests + future analytics. */
  reason: LifecycleOptiTimeDisplayReason;
  /** Echo of the helper result so callers needing detail (Tech /
   *  advisor delay sub-values, notes) don't recompute. */
  estimate: LifecycleOptiTimeEstimateResult;
}

const LABEL = 'Lifecycle OptiTime' as const;
const SUBLABEL = 'Estimated vehicle time in shop' as const;

const GENERIC_LOWER_BOUND_NOTE =
  'Lower-bound estimate: advisor delay is not available yet.';

/**
 * Map the Advisor Delay tile's refusal reason to a user-friendly
 * lower-bound note. Only invoked when `method === 'tech_only'`.
 */
function lowerBoundNoteFor(
  reason: AdvisorDelayImpactDisplayReason | null | undefined,
): string {
  switch (reason) {
    case 'no_handoff':
      return 'No advisor handoff activity yet; showing Tech OptiTime as the minimum vehicle time.';
    case 'not_approved':
      return 'Ticket not yet approved; advisor delay is calculated after approval.';
    case 'business_state_missing':
      return 'Approval state unavailable; lifecycle estimate is lower-bound only.';
    case 'classification_missing':
      return 'Job mix unavailable; lifecycle estimate is lower-bound only.';
    case 'overlap_unavailable':
      return 'Remaining approved work unavailable; lifecycle estimate is lower-bound only.';
    case 'dependency_deferred':
      return 'Hold/dependency tracking not available yet; lifecycle estimate is lower-bound only.';
    case 'value':
    case null:
    case undefined:
    default:
      return GENERIC_LOWER_BOUND_NOTE;
  }
}

/**
 * Build the display state for the Lifecycle OptiTime tile. Pure: same
 * inputs always produce the same output.
 */
export function buildLifecycleOptiTimeDisplayState(
  input: LifecycleOptiTimeDisplayInput,
): LifecycleOptiTimeDisplayState {
  const estimate = calculateLifecycleOptiTimeEstimate(input);

  switch (estimate.method) {
    case 'insufficient_data':
      return {
        shouldRender: true,
        label: LABEL,
        sublabel: SUBLABEL,
        valueMinutes: null,
        lowerBound: false,
        note: 'Tech OptiTime unavailable; lifecycle estimate cannot be calculated.',
        method: estimate.method,
        reason: 'insufficient_data',
        estimate,
      };

    case 'tech_only':
      return {
        shouldRender: true,
        label: LABEL,
        sublabel: SUBLABEL,
        valueMinutes: estimate.lifecycleOptiTimeMinutes,
        lowerBound: true,
        note: lowerBoundNoteFor(input.advisorDelayReason),
        method: estimate.method,
        reason: 'lower_bound_tech_only',
        estimate,
      };

    case 'tech_plus_advisor_delay':
      return {
        shouldRender: true,
        label: LABEL,
        sublabel: SUBLABEL,
        valueMinutes: estimate.lifecycleOptiTimeMinutes,
        lowerBound: false,
        // Slice 5D-8: "observed" qualifier added for symmetry with the
        // predicted-branch note below. Users now see "observed advisor
        // delay" here vs "predicted advisor delay" on the predicted
        // branch — the asymmetry was the most common source of "is
        // this a measurement or a forecast?" confusion in soak.
        note: 'Tech OptiTime + observed advisor delay.',
        method: estimate.method,
        reason: 'value',
        estimate,
      };

    case 'tech_plus_predicted_advisor_delay':
      return {
        shouldRender: true,
        label: LABEL,
        sublabel: SUBLABEL,
        valueMinutes: estimate.lifecycleOptiTimeMinutes,
        // Predicted estimates are NOT lower bounds — they are best-guess
        // forecasts based on history. Render the value plainly.
        lowerBound: false,
        note: buildPredictedAdvisorDelayNote(estimate.predictedAdvisorDelay),
        method: estimate.method,
        reason: 'predicted_advisor_delay',
        estimate,
      };
  }
}

/**
 * Build the user-friendly note shown under the Lifecycle OptiTime
 * value when prediction is in play. Avoids statistical jargon
 * (p50/p95/etc.) per slice 5D-4 spec — uses "average" + bucket label +
 * categorical confidence.
 *
 * Exported for unit testing — the wording is the load-bearing
 * "predicted, not observed" signal and is pinned by the drift detector.
 */
export function buildPredictedAdvisorDelayNote(
  predicted:
    | { bucket: string; bucketLabel?: string; confidence: string; sampleSize: number }
    | null
    | undefined,
): string {
  if (!predicted) {
    // Defensive — branch should not be reachable since the helper
    // attaches predictedAdvisorDelay whenever it uses this method.
    return 'Predicted advisor delay based on this advisor\'s recent average.';
  }
  const labelPart = predicted.bucketLabel
    ? predicted.bucketLabel
    : predicted.bucket.replace(/_/g, ' ');
  const confidenceWord = formatConfidenceLabel(predicted.confidence);
  const samplePart =
    predicted.sampleSize > 0
      ? ` (based on ${predicted.sampleSize} ticket${predicted.sampleSize === 1 ? '' : 's'})`
      : '';
  return `Predicted advisor delay · Based on this advisor's ${labelPart} average${samplePart} · Confidence: ${confidenceWord}.`;
}

function formatConfidenceLabel(confidence: string): string {
  switch (confidence) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    case 'insufficient':
    default:
      // Slice 5D-8: defensive — this branch is structurally
      // unreachable in normal flow because
      // `selectPredictedAdvisorDelayMinutes` filters out
      // `'insufficient'` confidence before we ever build a note. If a
      // future caller bypasses that filter, surface a user-friendly
      // string rather than the bare enum value.
      return 'Not enough advisor history yet';
  }
}

// ─── Slice 5D-8 — Lifecycle OptiTime delay-component breakdown ─────────

/**
 * Slice 5D-8: small "+Xm observed/predicted advisor delay" badge data
 * shown alongside the Lifecycle OptiTime value so the user can see
 * the delay component without expanding any other UI.
 *
 * Returns `null` when no breakdown should render (tech_only,
 * insufficient_data, or a zero-minute delay on either branch).
 *
 * Pure: no I/O. Exported for testing.
 */
export interface LifecycleOptiTimeDelayBadge {
  /** Minutes contributed by the advisor delay component. > 0. */
  minutes: number;
  /** Whether the contributing delay is observed (measured on this
   *  ticket's own custody history) or predicted (from this advisor's
   *  bucket average). Drives the badge's "observed" / "predicted"
   *  word. */
  kind: 'observed' | 'predicted';
}

export function getLifecycleOptiTimeDelayBadge(
  state: LifecycleOptiTimeDisplayState,
): LifecycleOptiTimeDelayBadge | null {
  const estimate = state.estimate;
  if (estimate.method === 'tech_plus_advisor_delay') {
    const m = estimate.advisorDelayImpactMinutes;
    if (typeof m === 'number' && Number.isFinite(m) && m > 0) {
      return { minutes: m, kind: 'observed' };
    }
    return null;
  }
  if (estimate.method === 'tech_plus_predicted_advisor_delay') {
    const m = estimate.predictedAdvisorDelay?.minutes ?? null;
    if (typeof m === 'number' && Number.isFinite(m) && m > 0) {
      return { minutes: m, kind: 'predicted' };
    }
    return null;
  }
  return null;
}
